import { Component, Element, Fragment, h, Prop, State } from '@stencil/core'
import { ComponentView } from '../../types/types'
import { AccountPanelOuter } from './panels/AccountPanelOuter'
import { AllInOnePanel } from './panels/AllInOnePanel'
import { ItemsPanelOuter } from './panels/ItemsPanelOuter'
import { SettingsPanel } from './panels/SettingsPanel'
import type { WidgetType } from './panels/WidgetPanel'
import { WidgetPanelOuter } from './panels/WidgetPanelOuter'
import { type Account, api, type Item } from './utils/api'
import {
  type ConfigError,
  type DebugConfig,
  type DebugSettings,
  type SelectedAccount,
  type SelectedItem,
  storage,
  type ToolbarPosition,
  type ToolbarSettings,
  type TrackingState
} from './utils/storage'
import { widgetManager } from './utils/widgets'
import { TrackingPanel } from './panels/TrackingPanel'

declare global {
  interface Window {
    RTR_ACCESS_TOKEN: string
  }
}

@Component({
  tag: 'rtr-debug-toolbar',
  styleUrl: 'rtr-debug-toolbar.css',
  shadow: false
})
export class RtrDebugToolbar {
  @Element() el: HTMLElement
  @Prop() widgetContainerId: string = 'rentware-widget-container'
  @Prop() checkoutContainerId: string = 'rentware-checkout-container'

  @State() accountsData: Account[] = []
  @State() itemsData: Item[] = []
  @State() currentPanel: string | null = null
  @State() selectedAccount: SelectedAccount | null = null
  @State() selectedItem: SelectedItem | null = null
  @State() isVisible: boolean = true
  @State() urlParams: string = 'None'
  @State() isLoadingAccount: boolean = false
  @State() isLoadingAccounts: boolean = false
  @State() isLoadingItems: boolean = false
  @State() accountsError: string | null = null
  @State() itemsError: string | null = null
  @State() statusMessage: string | null = null
  @State() widgetView: ComponentView = ComponentView.Default
  @State() widgetType: WidgetType = 'rtr-article-booking'
  @State() toolbarSettings: ToolbarSettings = { keepOpenOnClickOutside: false, position: 'bottom-center' }
  @State() favoritesOnly: boolean = false
  @State() favoritesAccountsData: Account[] = []
  @State() debugSettings: DebugSettings = { environment: 'localhost', staging: 'staging', apiBaseUrl: 'http://127.0.0.1:8000', publishedJs: false }
  @State() localStorageSize: number = 0
  @State() debugConfig: DebugConfig | null = null
  @State() configError: ConfigError | null = null
  @State() isLoadingConfig: boolean = false
  @State() trackingState: TrackingState = { clickId: undefined, referrer: undefined, customParam: undefined }

  private settingsChangeTimeout: number | null = null

  async componentWillLoad() {
    this.loadState()

    // Set the API URL immediately from saved settings to avoid URL construction errors
    window.RTR_API_URL_OVERWRITTEN = this.debugSettings.apiBaseUrl

    // Try to fetch debug config
    await this.loadDebugConfig()

    // Only initialize window variables and load accounts if we have config
    if (this.debugConfig) {
      storage.initializeWindowVariables(this.debugConfig.credentials)
      await this.loadAccounts()

      // Load items for the selected account if one exists
      if (this.selectedAccount) {
        await this.loadItems(this.selectedAccount.id)
      }
    }

    this.updateLocalStorageSize()
  }

  private async loadDebugConfig() {
    this.isLoadingConfig = true
    this.configError = null

    try {
      // Use the current debug settings to determine which base URL to try
      const baseUrl = this.debugSettings.apiBaseUrl
      this.debugConfig = await api.fetchDebugConfig(baseUrl)

      // Initialize window variables with credentials from config
      storage.initializeWindowVariables(this.debugConfig.credentials)
    } catch (error) {
      console.error('Failed to load debug config:', error)
      this.configError = {
        message: 'Debug toolbar configuration unavailable. This toolbar is only available in development and staging environments.',
        canRetry: true
      }
    } finally {
      this.isLoadingConfig = false
    }
  }

  private async retryConfigWithUrl(baseUrl: string) {
    // Update the debug settings with the new base URL
    const settings = { ...this.debugSettings, apiBaseUrl: baseUrl }
    this.debugSettings = settings
    storage.setDebugSettings(settings)
    window.RTR_API_URL_OVERWRITTEN = baseUrl

    // Try loading config again
    await this.loadDebugConfig()
  }

  private updateLocalStorageSize() {
    let totalBytes = 0
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key?.startsWith('rentware-debug-')) {
        const value = localStorage.getItem(key) || ''
        // Calculate bytes: key length + value length (each char is ~2 bytes in UTF-16)
        totalBytes += (key.length + value.length) * 2
      }
    }
    this.localStorageSize = Math.round(totalBytes / 1024) // in KB
  }

  async componentDidLoad() {
    document.addEventListener('keydown', this.handleKeydown)
    document.addEventListener('click', this.handleClickOutside)
    window.addEventListener('debug-settings-changed', this.handleDebugSettingsChange)

    if (this.selectedAccount?.widgetAccessToken) {
      window.RTR_ACCESS_TOKEN = this.selectedAccount.widgetAccessToken
    }

    this.updateDropdownSelections()

    // Render widgets after component is fully loaded to avoid race conditions
    if (this.selectedAccount) {
      await widgetManager.updateOrRenderWidgets(this.selectedItem, this.selectedAccount, this.widgetView, this.widgetType)
    }
  }

  componentDidUpdate() {
    this.updateDropdownSelections()
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.handleKeydown)
    document.removeEventListener('click', this.handleClickOutside)
    window.removeEventListener('debug-settings-changed', this.handleDebugSettingsChange)
  }

  // Event Handlers
  private handleKeydown = (e: KeyboardEvent) => {
    if (e.ctrlKey && e.shiftKey && e.key === 'D') {
      e.preventDefault()
      this.toggleToolbarVisibility()
    }
  }

  private handleClickOutside = (e: Event) => {
    if (!this.toolbarSettings.keepOpenOnClickOutside && !this.el.contains(e.target as Node)) {
      this.closeAllPanels()
    }
  }

  private handleDebugSettingsChange = async (event: Event) => {
    this.debugSettings = (event as CustomEvent<DebugSettings>).detail

    // Clear the previous timeout if exists
    if (this.settingsChangeTimeout !== null) {
      window.clearTimeout(this.settingsChangeTimeout)
    }

    // Debounce the account refresh to prevent multiple rapid fetches
    this.settingsChangeTimeout = window.setTimeout(async () => {
      // Clear selected item when environment changes
      this.selectedItem = null
      storage.setSelectedItem(null)

      // Clear selected account when environment changes
      this.selectedAccount = null
      storage.setSelectedAccount(null)
      window.RTR_ACCESS_TOKEN = ''

      // Clear items
      this.itemsData = []

      // Clear widgets
      widgetManager.clearWidgets()

      // Refresh accounts for the new environment
      await this.refreshAccounts()

      this.settingsChangeTimeout = null
    }, 300) // 300ms debounce
  }

  // State Management
  private loadState() {
    this.isVisible = storage.getToolbarVisible()
    this.selectedAccount = storage.getSelectedAccount()
    this.selectedItem = storage.getSelectedItem()
    this.widgetView = storage.getWidgetView()
    this.widgetType = storage.getWidgetType()
    this.toolbarSettings = storage.getToolbarSettings()
    this.favoritesOnly = storage.getFavoritesOnly()
    this.debugSettings = storage.getDebugSettings()
    this.updateFavoritesAccountsData()
  }

  private updateFavoritesAccountsData() {
    const favoriteIds = storage.getFavoriteAccounts()
    this.favoritesAccountsData = this.accountsData.filter(account => favoriteIds.includes(account.id))
  }

  private updateDropdownSelections() {
    const accountSelect = this.el.querySelector('#debugAccountSelect') as HTMLSelectElement
    if (accountSelect && this.selectedAccount) {
      accountSelect.value = this.selectedAccount.id
    }

    const itemSelect = this.el.querySelector('#debugItemSelect') as HTMLSelectElement
    if (itemSelect && this.selectedItem) {
      itemSelect.value = this.selectedItem.id
    }
  }

  // UI Actions
  private toggleToolbarVisibility() {
    this.isVisible = !this.isVisible
    storage.setToolbarVisible(this.isVisible)
  }

  private showStatusMessage(message: string, duration: number = 3000) {
    this.statusMessage = message
    setTimeout(() => {
      this.statusMessage = null
    }, duration)
  }

  private togglePanel(panelName: string) {
    this.currentPanel = this.currentPanel === panelName ? null : panelName
  }

  private closeAllPanels() {
    this.currentPanel = null
  }

  // Account Management
  private async loadAccounts() {
    const cacheKey = `rentware-debug-accountsData-${this.debugSettings.environment}-${this.debugSettings.staging}`
    const cached = localStorage.getItem(cacheKey)
    if (cached) {
      try {
        this.accountsData = JSON.parse(cached)
        this.updateFavoritesAccountsData()
        return
      } catch (e) {
        console.error('Failed to parse cached accounts', e)
      }
    }
    await this.refreshAccounts()
  }

  private async refreshAccounts() {
    this.isLoadingAccounts = true
    this.accountsError = null
    try {
      this.accountsData = await api.fetchAccounts()
      const cacheKey = `rentware-debug-accountsData-${this.debugSettings.environment}-${this.debugSettings.staging}`
      localStorage.setItem(cacheKey, JSON.stringify(this.accountsData))
      this.updateFavoritesAccountsData()
    } catch (error) {
      this.accountsError = error instanceof Error ? error.message : 'Failed to load accounts'
      console.error('Failed to fetch accounts:', error)
    } finally {
      this.isLoadingAccounts = false
    }
  }

  private async selectAccount(accountId: string) {
    this.isLoadingAccount = true

    this.selectedItem = null
    storage.setSelectedItem(null)

    try {
      if (!accountId) {
        storage.setSelectedAccount(null)
        window.RTR_ACCESS_TOKEN = ''
        this.selectedAccount = null
        this.itemsData = []
        widgetManager.clearWidgets()
        return
      }

      const account = this.accountsData.find(acc => acc.id === accountId)
      if (account) {
        this.selectedAccount = {
          id: accountId,
          name: account.name || '',
          widgetAccessToken: account.widgetAccessToken
        }
        storage.setSelectedAccount(this.selectedAccount)

        window.RTR_ACCESS_TOKEN = account.widgetAccessToken

        await this.loadItems(accountId)

        // Render widgets after loading items
        await widgetManager.updateOrRenderWidgets(this.selectedItem, this.selectedAccount, this.widgetView, this.widgetType)
      }
    } finally {
      this.isLoadingAccount = false
    }
  }

  private async setWidgetView(view: ComponentView) {
    this.widgetView = view
    storage.setWidgetView(view)
    if (this.selectedAccount) {
      await widgetManager.updateOrRenderWidgets(this.selectedItem, this.selectedAccount, view, this.widgetType)
    }
  }

  private async setWidgetType(type: WidgetType) {
    this.widgetType = type
    storage.setWidgetType(type)
    if (this.selectedAccount) {
      await widgetManager.updateOrRenderWidgets(this.selectedItem, this.selectedAccount, this.widgetView, type)
    }
  }

  // Item Management
  private async loadItems(accountId: string, forceRefresh = false) {
    this.isLoadingItems = true
    this.itemsError = null
    try {
      const cacheKey = `rentware-debug-account-items-${accountId}`
      const cachedItems = localStorage.getItem(cacheKey)
      if (cachedItems && !forceRefresh) {
        this.itemsData = JSON.parse(cachedItems)
        return
      }

      this.itemsData = await api.fetchItems(accountId)
      localStorage.setItem(cacheKey, JSON.stringify(this.itemsData))
    } catch (error) {
      this.itemsError = error instanceof Error ? error.message : 'Failed to load items'
      console.error('Failed to fetch items:', error)
    } finally {
      this.isLoadingItems = false
    }
  }

  private async refreshItems() {
    if (this.selectedAccount) {
      await this.loadItems(this.selectedAccount.id, true)
    } else {
      this.showStatusMessage('Please select an account first')
    }
  }

  private async selectItem(itemId: string, itemReference: string) {
    if (!itemId || !itemReference) {
      this.selectedItem = null
      storage.setSelectedItem(null)
      widgetManager.clearWidgets()
      await widgetManager.updateOrRenderWidgets(this.selectedItem, this.selectedAccount, this.widgetView, this.widgetType)
      return
    }

    const item = this.itemsData.find(i => i.id === itemId)
    this.selectedItem = {
      id: itemId,
      reference: itemReference,
      name: item?.name || ''
    }
    storage.setSelectedItem(this.selectedItem)

    widgetManager.clearWidgets()
    await widgetManager.updateOrRenderWidgets(this.selectedItem, this.selectedAccount, this.widgetView, this.widgetType)
  }

  // Settings
  private updateKeepOpenSetting(value: boolean) {
    this.toolbarSettings = { ...this.toolbarSettings, keepOpenOnClickOutside: value }
    storage.setToolbarSettings(this.toolbarSettings)
  }

  private updatePositionSetting(value: ToolbarPosition) {
    this.toolbarSettings = { ...this.toolbarSettings, position: value }
    storage.setToolbarSettings(this.toolbarSettings)
  }

  private updatePublishedJsSetting(value: boolean) {
    this.debugSettings.publishedJs = value
    storage.setDebugSettings(this.debugSettings)
  }

  private getPositionStyles(): Record<string, string> {
    const toolbarHeight = 40
    const panelOffset = 20
    const position = this.toolbarSettings.position
    const styles: Record<string, string> = {}
    if (position.includes('bottom')) {
      styles['--bottom'] = '0'
      styles['--bottom-panel'] = `${toolbarHeight + panelOffset}px`
    } else if (position.includes('top')) {
      styles['--top'] = '0'
      styles['--top-panel'] = `${toolbarHeight + panelOffset}px`
      styles['--translateY'] = '0'
    }

    if (position.includes('left')) {
      styles['--left'] = '0'
      styles['--left-panel'] = '10px'
    } else if (position.includes('right')) {
      styles['--right'] = '0'
      styles['--right-panel'] = '10px'
    } else if (position.includes('center')) {
      styles['--left'] = '50%'
      styles['--left-panel'] = '50%'
      styles['--translateX'] = '-50%'
      styles['--translateX-panel'] = '-50%'
    }

    return styles
  }

  private handleToggleFavorite = () => {
    // Storage is updated in AccountPanel's handleToggleFavorite
    // We just need to refresh the favorites list
    this.updateFavoritesAccountsData()
  }

  private resetAll() {
    if (!confirm('Reset all debug settings? This will clear all saved data.')) {
      return
    }
    storage.clearAll()
    location.reload()
  }

  
  // Tracking Management
  private handleTrackingChange(field: keyof TrackingState, value: string) {
    this.trackingState = { ...this.trackingState, [field]: value };
    storage.setTrackingState(this.trackingState);
  }

  private applyTracking() {
    const params = new URLSearchParams(window.location.search);

    if (this.trackingState.clickId) {
      const [key, value] = this.trackingState.clickId.includes('=')
        ? this.trackingState.clickId.split('=')
        : ['gclid', this.trackingState.clickId];
      params.set(key, value);
    }

    if (this.trackingState.customParam) {
      const [key, value] = this.trackingState.customParam.split('=');
      if (key && value) {
        params.set(key, value);
      }
    }

    window.history.pushState({}, '', window.location.pathname + '?' + params.toString());

    if (this.trackingState.referrer) {
      console.log('Referrer override set:', this.trackingState.referrer);
    }

    this.updateUrlParamsDisplay();
    alert('Tracking parameters applied!');
  }

  private clearTracking() {
    this.trackingState = {};
    storage.setTrackingState({});
    window.history.pushState({}, '', window.location.pathname);
    this.updateUrlParamsDisplay();
    alert('Tracking parameters cleared!');
  }

  private setRandomTracking() {
    const clickId = `gclid=${Math.random().toString(36).substring(2, 15)}`;
    const customParam = `utm_source=${Math.random().toString(36).substring(2, 15)}`;
    const referrer = `https://forestry.io/${Math.random().toString(36).substring(2, 15)}`;
    this.trackingState = {
      clickId,
      customParam,
      referrer,
    };
    storage.setTrackingState(this.trackingState);
    this.applyTracking();
  }

  private updateUrlParamsDisplay() {
    const params = new URLSearchParams(window.location.search);
    const paramsList: string[] = [];
    for (const [key, value] of params) {
      paramsList.push(`${key}=${value}`);
    }
    this.urlParams = paramsList.length > 0 ? paramsList.join(', ') : 'None';
  }

  private accountPanelProps() {
    return {
      accountsData: this.accountsData,
      selectedAccount: this.selectedAccount,
      onSelectAccount: (accountId: string) => this.selectAccount(accountId),
      onRefreshAccounts: () => this.refreshAccounts(),
      isLoadingAccount: this.isLoadingAccount,
      isLoadingAccounts: this.isLoadingAccounts,
      accountsError: this.accountsError,
      favoritesOnly: this.favoritesOnly,
      favoritesAccountsData: this.favoritesAccountsData,
      onToggleFavorite: this.handleToggleFavorite,
      debugConfig: this.debugConfig
    }
  }

  private itemsPanelProps() {
    return {
      itemsData: this.itemsData,
      selectedItem: this.selectedItem,
      onSelectItem: (id: string, ref: string) => this.selectItem(id, ref),
      onRefreshItems: () => this.refreshItems(),
      isLoadingAccount: this.isLoadingAccount,
      isLoadingItems: this.isLoadingItems,
      itemsError: this.itemsError,
      statusMessage: this.statusMessage
    }
  }

  private widgetPanelProps() {
    return {
      widgetType: this.widgetType,
      widgetView: this.widgetView,
      onWidgetTypeChange: (type: WidgetType) => this.setWidgetType(type),
      onWidgetViewChange: (view: ComponentView) => this.setWidgetView(view)
    }
  }

  private trackingPanelProps() {
    return {
      trackingState: this.trackingState,
      urlParams: this.urlParams,
      onTrackingChange: (field: keyof TrackingState, value: string) => this.handleTrackingChange(field, value),
      onApplyTracking: () => this.applyTracking(),
      onClearTracking: () => this.clearTracking(),
      onSetRandomTracking: () => this.setRandomTracking()
    }
  }

  private settingsPanelProps() {
    return {
      onResetAll: () => this.resetAll(),
      keepOpenOnClickOutside: this.toolbarSettings.keepOpenOnClickOutside,
      position: this.toolbarSettings.position,
      debugSettings: this.debugSettings,
      onKeepOpenChange: value => this.updateKeepOpenSetting(value),
      onPositionChange: value => this.updatePositionSetting(value),
      onPublishedJsChange: value => this.updatePublishedJsSetting(value),
      localStorageSize: this.localStorageSize,
      debugConfig: this.debugConfig
    }
  }

  private allInOnePanelProps() {
    return {
      ...this.accountPanelProps(),
      ...this.itemsPanelProps(),
      ...this.widgetPanelProps()
    }
  }

  private toolbarItems() {
    return [
      {
        name: 'All in One',
        icon: 'telescope',
        panel: 'all-in-one',
        dividerAfter: true
      },
      {
        name: 'Account',
        icon: 'book-user',
        panel: 'account'
      },
      {
        name: 'Items',
        icon: 'shopping-basket',
        panel: 'items'
      },
      {
        name: 'Widget',
        icon: 'layout-grid',
        panel: 'widget',
        dividerAfter: true
      },
      {
        name: 'Tracking',
        icon: 'activity',
        panel: 'tracking'
      },
      {
        name: 'Settings',
        icon: 'settings',
        panel: 'settings'
      }
    ]
  }

  render() {
    // Show error UI if config failed to load
    if (this.configError && !this.debugConfig) {
      return (
        <div
          class="debug-toolbar"
          style={this.getPositionStyles()}
          data-position={this.toolbarSettings.position}
          data-visible={this.isVisible}
        >
          <div class="debug-toolbar-container">
            <div
              class="debug-toolbar-panel open"
              style={{ maxWidth: '500px' }}
            >
              <div class="debug-toolbar-header">
                <h3>Debug Toolbar Error</h3>
              </div>
              <div class="debug-toolbar-section">
                <div
                  class="debug-toolbar-error"
                  style={{ padding: '12px', backgroundColor: '#fee', border: '1px solid #fcc', borderRadius: '4px' }}
                >
                  <p style={{ margin: '0 0 12px 0', color: '#c33' }}>{this.configError.message}</p>
                  <p style={{ margin: '0', fontSize: '14px', color: '#666' }}>
                    Current: <strong>{this.debugSettings.apiBaseUrl}</strong>
                  </p>
                </div>

                {this.configError.canRetry && (
                  <div style={{ marginTop: '16px' }}>
                    <p style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 'bold' }}>Try loading config from:</p>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        type="button"
                        class="debug-toolbar-button"
                        onClick={() => this.retryConfigWithUrl('http://127.0.0.1:8000')}
                        style={{ flex: '1' }}
                      >
                        Localhost
                      </button>
                      <button
                        type="button"
                        class="debug-toolbar-button"
                        onClick={() => this.retryConfigWithUrl('https://app.staging-new.rentware.io')}
                        style={{ flex: '1' }}
                      >
                        Staging
                      </button>
                    </div>
                    <button
                      type="button"
                      class="debug-toolbar-button"
                      onClick={() => this.loadDebugConfig()}
                      style={{ width: '100%', marginTop: '8px' }}
                    >
                      Retry Current URL
                    </button>
                  </div>
                )}

                <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f0f0f0', borderRadius: '4px' }}>
                  <p style={{ margin: '0', fontSize: '13px', color: '#666' }}>
                    Or visit{' '}
                    <a
                      href="/pages"
                      style={{ color: '#0066cc', textDecoration: 'underline' }}
                    >
                      /pages
                    </a>{' '}
                    to use the widgets without the debug toolbar.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    }

    // Show loading state while config is loading
    if (this.isLoadingConfig) {
      return (
        <div
          class="debug-toolbar"
          style={this.getPositionStyles()}
          data-position={this.toolbarSettings.position}
          data-visible={this.isVisible}
        >
          <div class="debug-toolbar-container">
            <div class="debug-toolbar-panel open">
              <div class="debug-toolbar-header">
                <h3>Debug Toolbar</h3>
              </div>
              <div class="debug-toolbar-section">
                <p>Loading configuration...</p>
              </div>
            </div>
          </div>
        </div>
      )
    }

    return (
      <div
        class="debug-toolbar"
        style={this.getPositionStyles()}
        data-position={this.toolbarSettings.position}
        data-visible={this.isVisible}
      >
        <div class="debug-toolbar-container">
          {this.toolbarItems().map(item => (
            <Fragment>
              <button
                type="button"
                class={`debug-toolbar-icon-button ${this.currentPanel === item.panel ? 'active' : ''}`}
                data-tooltip={item.name}
                onClick={() => this.togglePanel(item.panel)}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  stroke-width="1.5"
                >
                  <title>{item.name}</title>
                  <use href={`sprite.svg#${item.icon}`} />
                </svg>
              </button>

              {item.dividerAfter && <div class="debug-toolbar-separator" />}
            </Fragment>
          ))}
        </div>

        {this.currentPanel === 'all-in-one' && <AllInOnePanel {...this.allInOnePanelProps()} />}

        {this.currentPanel === 'account' && <AccountPanelOuter {...this.accountPanelProps()} />}

        {this.currentPanel === 'items' && <ItemsPanelOuter {...this.itemsPanelProps()} />}

        {this.currentPanel === 'widget' && <WidgetPanelOuter {...this.widgetPanelProps()} />}

        {this.currentPanel === 'tracking' && (
            <TrackingPanel {...this.trackingPanelProps()} />
          )}

        {this.currentPanel === 'settings' && <SettingsPanel {...this.settingsPanelProps()} />}
      </div>
    )
  }
}
